<template>
  <ContextColorProvider context="trade" class="paid-custody">
    <template v-if="hasError">
      <div class="paid-custody__cancel">
        <HelperViewStatus
          generic-error
          button-label="Tentar novamente"
          @click="fetchPaidCustody"
        />
      </div>
    </template>

    <div v-else-if="loading" class="loader">
      <SpinnerLoader :size="48" />
    </div>

    <template v-else>
      <header>
        <NavBar sticky>
          <template #left>
            <BackButton icon="EF0030" />
          </template>

          <template #right>
            <ButtonIconContextual
              icon="EF0051"
              label="Dúvidas frequentes"
              class="modal-close-button"
              @click="handleFrequentlyAskedQuestions"
            />
          </template>
        </NavBar>
      </header>

      <div class="paid-custody__container">
        <div>
          <BrandImage
            class="paid-custody__logo"
            data-testid="logo"
            :image="EBrandImageLogos.SYMBOL_PRIMARY"
            width="32px"
            :schema="EBrandImageSchemas.POSITIVE"
            as="span"
          />

          <Heading class="paid-custody__title">{{
            paidCustody?.title
          }}</Heading>
        </div>

        <div class="paid-custody-cards">
          <div
            v-for="card in paidCustody?.rows"
            :key="card.title"
            class="paid-custody-cards__container"
          >
            <InitialAvatarIcon
              :icon="card.icon"
              size="medium"
              class="paid-custody-cards__icon"
            />

            <div class="paid-custody-cards__container__content">
              <BaseText size="p3" as="p" :color="NebraskaColors.elementPrimary">
                {{ card.title }}
              </BaseText>
              <BaseText size="p5" :color="NebraskaColors.elementSecondary">
                {{ card.subtitle }}
              </BaseText>
            </div>
          </div>
        </div>
      </div>

      <SideSheetTerm
        :show-side-sheet="showSideSheet"
        :data="paidCustody?.term"
        @close="handleCloseModal"
        @openPassword="handleOpenModalPassword"
      />

      <ModalPassword
        :loading-password="loadingPassword"
        :is-open="showModalPassword"
        @close="handleCloseModalPassword"
        @confirm="goToNextStep"
      />

      <Splash />

      <footer class="paid-custody__footer">
        <ButtonContainer>
          <template #button-primary>
            <ButtonPrimary
              :label="hasError ? 'Ir para Trade' : 'Ativar custódia remunerada'"
              size="large"
              @click="handleNextClick"
            />
          </template>
        </ButtonContainer>
      </footer>
    </template>
  </ContextColorProvider>
</template>

<script setup lang="ts">
import { BackButton } from '@/modules/common/components/buttons';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  BrandImage,
  ButtonContainer,
  ButtonIconContextual,
  ButtonPrimary,
  ContextColorProvider,
  EBrandImageLogos,
  EBrandImageSchemas,
  Heading,
  HelperViewStatus,
  InitialAvatarIcon,
  NavBar,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { SideSheetTerm } from '@/modules/trade/views/PaidCustody/components/PaidCustodyActive/components/SideSheetTerm';
import { ModalPassword } from '@/modules/trade/views/PaidCustody/components/ModalPassword';
import { Splash } from '@/modules/trade/views/PaidCustody/components/PaidCustodyActive/components/Splash';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  getPaidCustody,
  PaidCustody
} from '@/modules/trade/services/paid-custody';

const { hasError, loadingPassword } = defineProps<{
  hasError: boolean;
  loadingPassword: boolean;
}>();

const router = useRouter();
const showSideSheet = ref(false);
const showModalPassword = ref(false);
const paidCustody = ref<PaidCustody | null>(null);
const loading = ref(false);

const emit = defineEmits(['has-error', 'go-to-next-step']);

const handleCloseModal = () => {
  showSideSheet.value = false;
};

const handleNextClick = () => {
  if (hasError) {
    router.push({ name: 'trade' });

    return notify({
      group: 'wrn-notifications',
      text: 'Houve um erro ao desativar a custódia remunerada. Tente novamente em alguns minutos.',
      type: NotificationTypes.error
    });
  }

  showSideSheet.value = true;
};

const handleOpenModalPassword = () => {
  showModalPassword.value = true;
};

const handleCloseModalPassword = () => {
  showModalPassword.value = false;
};

const handleFrequentlyAskedQuestions = () => {
  return router.push({ name: 'faq' });
};

const triggerError = () => {
  emit('has-error', true);
};

const goToNextStep = () => {
  emit('go-to-next-step');
};

const fetchPaidCustody = async () => {
  emit('has-error', false);

  try {
    loading.value = true;

    const response = await getPaidCustody();

    paidCustody.value = response;
  } catch (e) {
    triggerError();
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchPaidCustody();
});
</script>

<style scoped lang="less">
.paid-custody {
  overflow: hidden;
  position: relative;
  height: 100vh;

  // TO-DO Remover quando atualizar o nebraska para v5.37.0
  :deep(.navbar-container) {
    border: none !important;
  }

  &__container {
    max-width: 832px;
    height: calc(100vh - @size-spacing-x1000);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-grow: 1;
    margin: 0 auto;
    padding-bottom: @size-spacing-x1400;
    .to-md({
      padding: 0 @size-spacing-x400 @size-spacing-x1400 @size-spacing-x400 ;
      flex-direction: column;
      justify-content: center;
      gap: @size-spacing-x1000;
    });
  }

  &__cancel {
    height: calc(100vh - @size-spacing-x1200);
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: @size-spacing-x1200;
    :deep(.helper-status) {
      max-width: 492px;
    }

    :deep(.helper-view__icon) {
      margin-bottom: @size-spacing-x600;
    }

    :deep(.initial-avatar-icon) {
      background: @status-alert;
    }

    :deep(.helper-view__title p) {
      font-size: 40px;
      line-height: 48px;
      font-weight: 400;
    }
  }

  &__logo {
    margin-bottom: @size-spacing-x600;
  }

  &__title {
    max-width: 366px;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: @background-secondary;
  }

  .loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
  }
}

.paid-custody-cards {
  max-width: 406px;
  display: flex;
  flex-direction: column;
  gap: @size-spacing-x500;

  &__icon {
    min-width: @size-spacing-x800;
    margin: @size-spacing-x150;
  }

  &__container {
    display: flex;
    gap: @size-spacing-x400;

    &__content {
      display: flex;
      flex-direction: column;
      gap: @size-spacing-x150;
    }
  }
}
</style>
